import { track as amplitudeTrack } from '@amplitude/analytics-browser';
import { addBreadcrumb } from '@sentry/react';

/**
 * Client-side audit trail for money movement.
 *
 * Every surface that moves money should leave the same breadcrumbs, so an
 * incident can be reconstructed from Amplitude or from the Sentry breadcrumbs
 * attached to whatever exception followed. Built as a factory because credit
 * repayment and debit funding are separate namespaces but must behave
 * identically; the logger NEVER throws, so a broken analytics call can never
 * take a payment down with it.
 */

export interface AuditContext {
  userId?: string | null;
  paidolId?: string | null;
  financialAccountId?: string | null;
}

export type AuditLogger<TEvent extends string> = (
  event: TEvent,
  details?: Record<string, unknown>,
  context?: AuditContext
) => void;

export function createAuditLogger<TEvent extends string>(namespace: string): AuditLogger<TEvent> {
  return (event, details = {}, context = {}) => {
    try {
      const name = `${namespace}.${event}`;
      const payload = {
        ...details,
        _ctx: context,
        _ts: new Date().toISOString(),
      };

      try {
        // eslint-disable-next-line no-console
        console.log(`[audit] ${name}`, payload);
      } catch {
        // ignore
      }

      try {
        amplitudeTrack(name, payload);
      } catch {
        // ignore
      }

      try {
        addBreadcrumb({
          category: 'money-audit',
          type: 'info',
          level: 'info',
          message: name,
          data: payload,
        });
      } catch {
        // ignore
      }
    } catch {
      // never throw from the audit path
    }
  };
}
